import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { useWhiteLabel } from "@/contexts/WhiteLabelContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { 
  ArrowLeft, User, Mail, Shield, Trash2, AlertTriangle,
  Crown, Calendar, Key, Bell, HelpCircle, FileText, Lock, ChevronRight
} from "lucide-react";
import { SupportContact } from "@/components/LegalDisclaimers";

/**
 * Account Settings Page
 * Includes DPDP Act compliant data deletion mechanism
 */
const AccountSettingsPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isWhiteLabel, getInstituteName, getSupportPhone } = useWhiteLabel();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteRequested, setDeleteRequested] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteRequest = async () => {
    setIsDeleting(true);
    
    // Simulate sending deletion request
    // In production, this would call an API endpoint
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setDeleteRequested(true);
    setIsDeleting(false);
    setShowDeleteConfirm(false);
    
    toast({
      description: "Your account deletion request has been submitted. You will receive a confirmation email shortly.",
    });
  };

  const handleLogout = () => {
    logout();
    navigate("/auth");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-6 py-4">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to Dashboard</span>
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-6 py-8">
        <h1 className="text-2xl font-bold text-slate-900 mb-6">Account Settings</h1>

        <div className="space-y-6">
          {/* Profile Section */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center">
                  <User className="w-8 h-8 text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">{user?.name || "User"}</h2>
                  <p className="text-sm text-slate-500">{user?.email}</p>
                  <div className="flex items-center gap-2 mt-1">
                    {user?.role === "PRO" ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                        <Crown className="w-3 h-3" /> PRO
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs font-medium rounded-full">
                        FREE
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-slate-500" />
                    <div>
                      <p className="text-sm font-medium text-slate-700">Email</p>
                      <p className="text-xs text-slate-500">{user?.email}</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-slate-500" />
                    <div>
                      <p className="text-sm font-medium text-slate-700">Member Since</p>
                      <p className="text-xs text-slate-500">
                        {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : "N/A"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Legal Section */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-slate-600" />
                Legal
              </h3>
              
              <div className="space-y-2">
                <Link 
                  to="/terms-of-service"
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <span className="text-sm text-slate-700">Terms of Service</span>
                  <ArrowLeft className="w-4 h-4 text-slate-400 rotate-180" />
                </Link>
                <Link 
                  to="/privacy-policy"
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <span className="text-sm text-slate-700">Privacy Policy</span>
                  <ArrowLeft className="w-4 h-4 text-slate-400 rotate-180" />
                </Link>
                <Link 
                  to="/refund-policy"
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <span className="text-sm text-slate-700">Refund & Cancellation Policy</span>
                  <ArrowLeft className="w-4 h-4 text-slate-400 rotate-180" />
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Support Section */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-slate-600" />
                Support
              </h3>
              <SupportContact variant="card" />
              <p className="text-xs text-slate-500 mt-3">
                Typical response time: 24–48 business hours.
              </p>
              <Link 
                to="/support"
                className="mt-3 inline-flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-700 font-medium"
              >
                View full support info
                <ArrowLeft className="w-4 h-4 rotate-180" />
              </Link>
            </CardContent>
          </Card>

          {/* Data & Privacy Section - DPDP ACT COMPLIANCE */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Lock className="w-5 h-5 text-slate-600" />
                Data & Privacy
              </h3>
              
              <div className="space-y-4">
                <p className="text-sm text-slate-600">
                  Under the Digital Personal Data Protection Act, 2023, you have the right to request 
                  deletion of your personal data and account.
                </p>

                {deleteRequested ? (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <Shield className="w-5 h-5 text-emerald-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-emerald-800">Deletion Request Submitted</p>
                        <p className="text-sm text-emerald-700 mt-1">
                          Your request to delete your account and associated data has been received. 
                          We will process it as per applicable data protection laws.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {!showDeleteConfirm ? (
                      <Button
                        variant="outline"
                        onClick={() => setShowDeleteConfirm(true)}
                        className="w-full justify-start text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete My Account
                      </Button>
                    ) : (
                      <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-4">
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                          <div>
                            <p className="font-medium text-red-800">Are you sure?</p>
                            <p className="text-sm text-red-700 mt-1">
                              This will permanently delete your account and all associated data, including:
                            </p>
                            <ul className="text-sm text-red-600 mt-2 space-y-1 ml-4 list-disc">
                              <li>Your profile information</li>
                              <li>Test history and progress data</li>
                              <li>Saved answers and notes</li>
                              <li>Subscription information</li>
                            </ul>
                            <p className="text-sm text-red-700 mt-2">
                              This action cannot be undone.
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex gap-3">
                          <Button
                            variant="outline"
                            onClick={() => setShowDeleteConfirm(false)}
                            className="flex-1"
                          >
                            Cancel
                          </Button>
                          <Button
                            onClick={handleDeleteRequest}
                            disabled={isDeleting}
                            className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                          >
                            {isDeleting ? (
                              <span className="flex items-center gap-2">
                                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                Processing...
                              </span>
                            ) : (
                              "Yes, Delete My Account"
                            )}
                          </Button>
                        </div>
                      </div>
                    )}

                    <p className="text-xs text-slate-500">
                      Alternatively, you can request data deletion by emailing{" "}
                      <a href="mailto:support@clarias.in" className="text-indigo-600 hover:underline">
                        support@clarias.in
                      </a>
                    </p>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Legal Section - Mobile App Compliance */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="w-5 h-5 text-slate-600" />
                Legal
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <a 
                href="/terms-of-service" 
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <span className="text-sm text-slate-700">Terms of Service</span>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </a>
              <a 
                href="/privacy-policy" 
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <span className="text-sm text-slate-700">Privacy Policy</span>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </a>
              <a 
                href="/refund-policy" 
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <span className="text-sm text-slate-700">Refund Policy</span>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </a>
              <p className="text-xs text-slate-500 pt-2">
                By using CLARIAS, you agree to our terms and policies.
              </p>
            </CardContent>
          </Card>

          {/* Help & Support Section */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <Link 
                to="/help"
                className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-green-100">
                    <HelpCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Help & Support</p>
                    <p className="text-sm text-slate-500">FAQs, contact us, WhatsApp support</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </Link>
            </CardContent>
          </Card>

          {/* Logout Section */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <Button
                variant="outline"
                onClick={handleLogout}
                className="w-full"
              >
                Log Out
              </Button>
            </CardContent>
          </Card>

          {/* Powered by CLARIAS - For White-Label */}
          {isWhiteLabel && (
            <Card className="border-0 shadow-sm bg-gradient-to-r from-orange-50 to-amber-50">
              <CardContent className="p-6">
                <div className="text-center">
                  <p className="text-sm text-slate-600">
                    Platform powered by
                  </p>
                  <p className="text-xl font-bold text-orange-600 mt-1">
                    CLARIAS Intelligence
                  </p>
                  <p className="text-xs text-slate-500 mt-2">
                    UPSC-trained preparation technology
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Institute Support - For White-Label */}
          {isWhiteLabel && getSupportPhone() && (
            <Card className="border-0 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-900">{getInstituteName()} Support</p>
                    <p className="text-sm text-slate-500">Contact your institute for assistance</p>
                  </div>
                  <a 
                    href={`tel:${getSupportPhone()}`}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium"
                  >
                    Call Support
                  </a>
                </div>
              </CardContent>
            </Card>
          )}

        </div>
      </div>
    </div>
  );
};

export default AccountSettingsPage;
